import type {ReactElement} from 'react';
import React, {useContext} from 'react';
import {Text, TextSize} from '@Components/text';
import type {Component, Editor} from 'grapesjs';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import type {EmailMakerActions, EmailMakerBrandKitSuggestionsTarget, EmailMakerImmutableStore, EmailMakerState} from '@EmailMakerReact/email-maker-react.types';
import {RightPanelView} from '@EmailMakerReact/email-maker-react.types';
import {useSelectedComponent} from '@EmailMakerReact/hooks/use-selected-component';
import {useImageCropMode} from '@EmailMakerReact/hooks/use-image-crop-mode';
import {applyAutoBrandingStyle} from '@EmailMakerReact/lib/email-maker-ai-library';
import {getEditableHtml} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {updateColumnsPreset} from '@EmailMakerReact/lib/email-maker-table-library';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerAdminControls} from './components/email-maker-admin-controls/email-maker-admin-controls';
import {AdminControlsScope} from './components/email-maker-admin-controls/email-maker-admin-controls.types';
import type {ColorPaletteInfo, ColorPalettePill} from '@Libraries/color-palettes.library';
import {getGA4StyleTypeFromColorPalette, storeAppliedPaletteInHtml} from '@Libraries/auto-branding-email-maker';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {setColorPaletteBeingApplied, setSelectedColorPalette} from '@/reducers/color-palettes.reducer';
import {RightPanelLoadingSkeleton} from './components/right-panel-loading-skeleton/right-panel-loading-skeleton';
import {RightPanelSectorTitle} from './components/right-panel-sector-title/right-panel-sector-title';
import {resolveSectorsForComponent, resolveSectorTitle} from './right-panel-registry';
import {resolveControlTarget} from '@EmailMakerReact/lib/email-maker-tile-meta-library';
import {
  getAppliedPaletteFromWrapper,
  groupToolbarButtons,
  isCollapsibleControl,
  isIconButtonControl,
  resolveColumnsPresetId,
  shouldUpdateColorPaletteOnApplication,
} from './email-maker-right-panel.util';
import {KeyboardShortcutsView} from './views/keyboard-shortcuts-view/keyboard-shortcuts-view';
import {ColorPaletteView} from './views/color-palette-view/color-palette-view';
import {BrandKitSuggestionsView} from './views/brand-kit-suggestions-view/brand-kit-suggestions-view';
import {ColorPickerView} from './views/color-picker-view/color-picker-view';
import {ColumnsAllView} from './views/columns-all-view/columns-all-view';
import {VersionHistoryView} from './views/version-history-view/version-history-view';
import {ImageCropView} from './views/image-crop-view/image-crop-view';
import type {EmailMakerControl, EmailMakerSectorEntry, EmailMakerSectorVisibilityCtx, IndexedSectorProperty} from './right-panel-registry.types';
// Side-effect imports — registration order is significant; each call to registerEmailMakerSector appends to REGISTRY.
import './sectors/multiselect-sector/multiselect-sector';
import './sectors/table-sector/table-sector';
import './sectors/read-only-footer-sector/read-only-footer-sector';
import './sectors/section-sector/section-sector';
import './sectors/socials-item-sector/socials-item-sector';
import './sectors/socials-sector/socials-sector';
import './sectors/static-cell-sector/static-cell-sector';
import './sectors/cell-sector/cell-sector';
import './sectors/divider-sector/divider-sector';
import './sectors/countdown-sector/countdown-sector';
import './sectors/wrapper-sector/wrapper-sector';
import './sectors/design-sector/design-sector';
import './sectors/media-sector/media-sector';
import './sectors/button-sector/button-sector';
import './sectors/text-sector/text-sector';
import './sectors/general-sector/general-sector';
import styles from './email-maker-right-panel.module.scss';

export function EmailMakerRightPanel(): ReactElement {
  const {editor, store, state, actions} = useContext(emailMakerContext);
  const selected = useSelectedComponent();
  const imageCropState = useImageCropMode(editor);

  if (imageCropState && editor) {
    return (
      <aside aria-label={window.i18next.t('pmwjs_email_maker_properties_panel_aria')} className={styles.panel}>
        <ImageCropView editor={editor} cropState={imageCropState} />
      </aside>
    );
  }

  if (state.rightPanelView === RightPanelView.KEYBOARD_SHORTCUTS) {
    return (
      <aside aria-label={window.i18next.t('pmwjs_email_maker_properties_panel_aria')} className={styles.panel}>
        <KeyboardShortcutsView onBack={actions.closeRightPanelView} />
      </aside>
    );
  }

  if (state.rightPanelView === RightPanelView.COLOR_PALETTE) {
    return (
      <aside aria-label={window.i18next.t('pmwjs_email_maker_properties_panel_aria')} className={styles.panel}>
        {renderColorPaletteView(editor, actions)}
      </aside>
    );
  }

  if (state.rightPanelView === RightPanelView.BRAND_KIT_SUGGESTIONS && editor && state.brandKitSuggestionsTarget) {
    return (
      <aside aria-label={window.i18next.t('pmwjs_email_maker_properties_panel_aria')} className={styles.panel}>
        {renderBrandKitSuggestionsView(editor, state.brandKitSuggestionsTarget, actions)}
      </aside>
    );
  }

  if (state.rightPanelView === RightPanelView.COLOR_PICKER && editor && state.colorPickerTarget) {
    return (
      <aside aria-label={window.i18next.t('pmwjs_email_maker_properties_panel_aria')} className={styles.panel}>
        <ColorPickerView editor={editor} target={state.colorPickerTarget} onBack={actions.closeRightPanelView} />
      </aside>
    );
  }

  if (state.rightPanelView === RightPanelView.COLUMNS_ALL && selected && editor) {
    return (
      <aside aria-label={window.i18next.t('pmwjs_email_maker_properties_panel_aria')} className={styles.panel}>
        {renderColumnsAllView(selected, editor, actions)}
      </aside>
    );
  }

  if (state.rightPanelView === RightPanelView.VERSION_HISTORY && editor) {
    return (
      <aside aria-label={window.i18next.t('pmwjs_email_maker_properties_panel_aria')} className={styles.panel}>
        <VersionHistoryView editor={editor} onClose={actions.closeRightPanelView} />
      </aside>
    );
  }

  const sectors = selected && editor && !state.isEditorLoading ? resolveSectorsForComponent(editor, selected, store) : null;

  return (
    <aside aria-label={window.i18next.t('pmwjs_email_maker_properties_panel_aria')} className={styles.panel}>
      {renderHeader(selected, editor, state, sectors)}
      <div className={styles.body}>{renderBody(state.isEditorLoading, selected, editor, store, sectors)}</div>
    </aside>
  );
}

/** The PMW wrapper hosts the global admin block; a non-wrapper, non-section selection hosts the per-component block. */
function isAdminWrapperComponent(component: Component): boolean {
  return component.get('type') === EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER;
}

function isAdminSectionComponent(component: Component): boolean {
  return component.get('type') === EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION;
}

/** Per-component admin block, appended after the resolved sectors for a non-wrapper, non-section selection. */
function renderPerComponentAdminControls(selected: Component, editor: Editor, store: EmailMakerImmutableStore): ReactElement | null {
  if (isAdminSectionComponent(selected)) {
    return null;
  }
  return <EmailMakerAdminControls scope={AdminControlsScope.COMPONENT} editor={editor} component={selected} idEmailTemplate={store.idEmailTemplate} />;
}

/** Global admin block, shown for the wrapper selection and on the empty state. */
function renderGlobalAdminControls(editor: Editor | null, store: EmailMakerImmutableStore): ReactElement | null {
  if (!store.isSuperUser || !editor) {
    return null;
  }
  return <EmailMakerAdminControls scope={AdminControlsScope.GLOBAL} editor={editor} idEmailTemplate={store.idEmailTemplate} />;
}

function renderColumnsAllView(selected: Component, editor: Editor, actions: EmailMakerActions): ReactElement {
  const selectedPresetId = resolveColumnsPresetId(selected.get(EMAIL_MAKER_PROPS.COLUMNS_PRESET));

  function handlePresetSelect(presetId: number): void {
    selected.set(EMAIL_MAKER_PROPS.COLUMNS_PRESET, presetId);
    updateColumnsPreset(editor, presetId, selected);
    actions.closeRightPanelView();
  }

  return <ColumnsAllView component={selected} onBack={actions.closeRightPanelView} onPresetSelect={handlePresetSelect} selectedPresetId={selectedPresetId} />;
}

function renderBrandKitSuggestionsView(editor: Editor, target: EmailMakerBrandKitSuggestionsTarget, actions: EmailMakerActions): ReactElement {
  function handleSuggestedPaletteSelect(palette: ColorPaletteInfo): void {
    window.PMW.redux.store.dispatch(setColorPaletteBeingApplied(palette));
    window.PMW.redux.store.dispatch(setSelectedColorPalette(palette));
    void applyAutoBrandingStyle(editor, {
      colorPalette: palette,
      html: getEditableHtml(editor),
      onSuccess: storeAppliedPaletteInHtml,
    });
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_STYLES_APPLIED, {[GA4EventParamName.TYPE]: getGA4StyleTypeFromColorPalette(palette)});
  }

  return <BrandKitSuggestionsView target={target} onSuggestedPaletteSelect={handleSuggestedPaletteSelect} onBack={actions.openRightPanelColorPalette} />;
}

function renderColorPaletteView(editor: Editor | null, actions: EmailMakerActions): ReactElement {
  const appliedColorPalette = editor ? getAppliedPaletteFromWrapper(editor) : null;

  function handleColorPaletteSelect(palette: ColorPaletteInfo): void {
    if (!editor) {
      return;
    }
    void applyAutoBrandingStyle(editor, {
      colorPalette: palette,
      html: getEditableHtml(editor),
      onSuccess: storeAppliedPaletteInHtml,
      updateColorPalette: shouldUpdateColorPaletteOnApplication(palette),
      onUpdatedColorPalette: (appliedPalette: ColorPaletteInfo, suggestedPalettes: ColorPaletteInfo[]): void => {
        storeAppliedPaletteInHtml(editor, appliedPalette);
        actions.openRightPanelBrandKitSuggestions({brandKitPalette: palette, suggestedPalettes});
      },
    });
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_STYLES_APPLIED, {[GA4EventParamName.TYPE]: getGA4StyleTypeFromColorPalette(palette)});
  }

  function handlePillSelected(pill: ColorPalettePill): void {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_STYLES_PILLS, {[GA4EventParamName.TERM]: pill.name});
  }

  function handleSearchSubmit(term: string): void {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_STYLES_SEARCHED, {[GA4EventParamName.SEARCH_TERM]: term});
  }

  return (
    <ColorPaletteView
      appliedColorPalette={appliedColorPalette}
      onBack={actions.closeRightPanelView}
      onColorPaletteSelect={handleColorPaletteSelect}
      onPillSelected={handlePillSelected}
      onSearchSubmit={handleSearchSubmit}
    />
  );
}

function renderHeader(selected: Component | null, editor: Editor | null, state: EmailMakerState, sectors: EmailMakerSectorEntry[] | null): ReactElement | null {
  if (!selected || !editor || state.isEditorLoading || !sectors) {
    return null;
  }
  if (sectors.length === 0) {
    return null;
  }
  const entry = sectors[0];
  if (entry?.headerControl) {
    return <header className={styles.header}>{renderControl(entry.headerControl, selected, 0)}</header>;
  }
  if (entry?.hideTitle) {
    return null;
  }
  const title = resolveSectorTitle(entry, selected);
  return (
    <header className={styles.header}>
      <RightPanelSectorTitle title={title} />
    </header>
  );
}

function renderBody(
  isEditorLoading: boolean,
  selected: Component | null,
  editor: Editor | null,
  store: EmailMakerImmutableStore,
  sectors: EmailMakerSectorEntry[] | null
): ReactElement | null {
  if (isEditorLoading) {
    return <RightPanelLoadingSkeleton />;
  }
  return renderSelectedSectors(selected, editor, store, sectors);
}

function renderSelectedSectors(selected: Component | null, editor: Editor | null, store: EmailMakerImmutableStore, sectors: EmailMakerSectorEntry[] | null): ReactElement | null {
  if (!selected || !editor || !sectors) {
    return renderEmpty(editor, store);
  }
  if (sectors.length === 0) {
    return <Text val={window.i18next.t('pmwjs_email_maker_no_controls_for_type')} size={TextSize.MD} className={styles.placeholder} />;
  }
  return (
    <>
      {sectors.map((entry, idx) => renderSector(selected, entry, idx, editor, store))}
      {renderAdminControlsForSelection(selected, editor, store)}
    </>
  );
}

/** The wrapper selection hosts the global admin block; any other selection hosts the per-component block. */
function renderAdminControlsForSelection(selected: Component, editor: Editor, store: EmailMakerImmutableStore): ReactElement | null {
  if (!store.isSuperUser) {
    return null;
  }
  if (isAdminWrapperComponent(selected)) {
    return renderGlobalAdminControls(editor, store);
  }
  return renderPerComponentAdminControls(selected, editor, store);
}

function renderEmpty(editor: Editor | null, store: EmailMakerImmutableStore): ReactElement {
  return (
    <>
      <Text val={window.i18next.t('pmwjs_email_maker_select_element_to_edit')} size={TextSize.SM} className={styles.empty} />
      {renderGlobalAdminControls(editor, store)}
    </>
  );
}

function renderSector(selected: Component, entry: EmailMakerSectorEntry, sectorIdx: number, editor: Editor, store: EmailMakerImmutableStore): ReactElement {
  const ctx: EmailMakerSectorVisibilityCtx = {component: selected, editor, store};
  const visibleProperties = entry.properties.map((property, stableIndex) => ({property, stableIndex})).filter(({property}) => !property.isVisible || property.isVisible(ctx));
  return (
    <div key={sectorIdx}>
      {groupToolbarButtons(visibleProperties).map((group) => renderPropertyGroup(selected, group, editor))}
    </div>
  );
}

function renderPropertyGroup(selected: Component, group: IndexedSectorProperty[], editor: Editor): ReactElement {
  const firstIndexed = group[0];
  if (group.length === 1 && firstIndexed !== undefined && !isIconButtonControl(firstIndexed.property.control)) {
    return renderProperty(selected, firstIndexed, editor);
  }
  const groupKey = firstIndexed !== undefined ? firstIndexed.stableIndex : 0;
  return (
    <div className={styles.toolbar} key={groupKey}>
      {group.map((indexed) => renderToolbarButton(selected, indexed, editor))}
    </div>
  );
}

function renderToolbarButton(selected: Component, {property, stableIndex}: IndexedSectorProperty, editor: Editor): ReactElement {
  const {control, resolveTarget} = property;
  const target = resolveControlTarget(selected, resolveTarget, editor);
  return (
    <div className={styles.toolbarCell} key={stableIndex}>
      {renderControl(control, target, stableIndex)}
    </div>
  );
}

function getRowClassName(control: EmailMakerControl): string {
  if (isCollapsibleControl(control)) {
    return styles.collapsibleRow;
  }
  return styles.row;
}

function renderControl(control: EmailMakerControl, component: Component, key: number): ReactElement {
  return React.createElement(control, {component, key});
}

function renderProperty(selected: Component, {property, stableIndex}: IndexedSectorProperty, editor: Editor): ReactElement {
  const {control, resolveTarget} = property;
  const target = resolveControlTarget(selected, resolveTarget, editor);
  return (
    <div className={getRowClassName(control)} key={stableIndex}>
      {renderControl(control, target, stableIndex)}
    </div>
  );
}
